/**
* A service to interact with the progress indicator.
* Interactions modes include both setting the visbility explicitly
* and making the service aware of tasks it should wait on. 
*/
export class ProgressService {
	public static $inject = [
		'$timeout',
		'$q'
	];

	/**
	 * Queue of outstanding tasks that must be completed before
	 * the progress indicator should be removed.
	 */
	private queue:ng.IPromise<any>[] = [];
	/**
	* Ref count of the number of consumers asking to show the indicator.
	*/ 
	private callCount:number = 0;
	private _visible:boolean = false;
	private hidingTimer:ng.IPromise<void>;

	public text:string;
	public backdrop:boolean = false;
	public target:Element;

	/**
	* @ngdoc property
	* @name pxProgressService#visible
	* @propertyOf phoenix.pxProgressService
	* @kind property
	*
	* @description
	* The visibility of the progress indicator.
	*
	* @returns {bool} Indicates whether the progress indicator is 
	* visible
	*/
	get visible() { return this._visible };	
	set visible(val) {
		if(val){
			this.callCount++;
		}
		else {
			this.callCount--;
		}			
	
		if(this.callCount > 0){
			this.show();
		}
		else {
			this.callCount = 0;
			this.hide();			
		}
	}


	constructor(
		private $timeout:ng.ITimeoutService,
		private $q:ng.IQService
	) {
	}

	/**
	 * Add tasks that the progress indicator should be visible for and wait on.
	 */
	public enqueue(...progressTasks:ng.IPromise<any>[]){
		if(progressTasks.length < 1) {
			return;
		}
		
		// don't directly hold a reference to externally-provided promises 
		// to prevent function scopes from living indefinitely
		progressTasks = progressTasks.map(t => {
			let p = this.$q((resolve, reject) => {
				t.then(resolve).catch(reject);
			});
			p.finally(() => this.onTaskCompleted(p));

			// release reference to original promise so it can be GC'd when original scope dies
			t = null;
			return p;
		});
		this.queue.push(...progressTasks);
		
		/*
			Placed the call to show() in a timeout in order to see if the promises were already resolved,
			in which case they would be removed from the queue and there'd be no need to show the
			progress indicator
		*/
		this.$timeout(() => {
			if(this.queue.length > 0) {
				this.show();
			}
		}, 0);
	}

	/**
	 * Hide the progress indictator if there are no oustanding tasks or consumers.
	 */
	public hide(forceHide:boolean = false){	
		let canHide = forceHide || (this.callCount === 0 && this.queue.length === 0);
		if(!this.hidingTimer && canHide){
			// use a timeout to ensure progress indicator fully displays before hiding
			this.hidingTimer = this.$timeout(() => {
				this.backdrop = this._visible = false;
				this.hidingTimer = this.text = this.target = null;		
				this.queue = [];
				this.callCount = 0;
			}, 700);
		}
	}

	/**
	 * Show the progress indictator irrespective of any outstanding tasks or callers.
	 */
	private show(){
		if(this.hidingTimer){
			this.$timeout.cancel(this.hidingTimer);
			this.hidingTimer = null;
		}

		this._visible = true;
	}

	private removeTask(task:ng.IPromise<any>){
		let i = this.queue.indexOf(task);
		if( i < 0){
			return;
		}

		this.queue.splice(i,1);
	}

	private onTaskCompleted(task:ng.IPromise<any>){
		this.removeTask(task);
		if(this.queue.length === 0){
			this.hide();
		}
	}
}



// WEBPACK FOOTER //
// ./src/phoenix/services/progressService.ts